import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Sparkles, GraduationCap, Loader2 } from "lucide-react";
import { PageHero, Reveal, Eyebrow } from "@/components/Reveal";
import { WaveMark } from "@/components/Illustrations";
import { API } from "@/lib/site";

const INTERESES = [
  { id: "clienta", label: "Quiero cuidarme con la Técnica NAGI", Icon: Sparkles },
  { id: "alumna", label: "Quiero aprender y formarme con la Técnica NAGI", Icon: GraduationCap },
];

const Academia = () => {
  const [form, setForm] = useState({ nombre: "", telefono: "", email: "", interes: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.interes) {
      setError("Elige qué te trae por aquí.");
      return;
    }
    setStatus("loading");
    setError("");
    try {
      const res = await fetch(`${API}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setStatus("done");
    } catch {
      setStatus("idle");
      setError("No pudimos guardar tus datos. Inténtalo de nuevo en un momento.");
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-cream/25 focus:border-gold outline-none py-3 text-cream placeholder:text-cream/35 transition-colors duration-300 font-light";

  return (
    <div data-testid="academia-page">
      <PageHero
        dark
        eyebrow="Academia Karina Méndez · Próximamente"
        lines={["Algo increíble", "está por suceder."]}
        sub="La Academia Karina Méndez está casi lista. Y quiero compartirla contigo antes que con nadie más. Sé la primera en saberlo, déjame tus datos y llévate una sorpresa por decidirte a tiempo."
      />

      <section className="bg-ink text-cream px-6 sm:px-12 lg:px-20 pb-24 lg:pb-36" data-testid="academia-waitlist">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-4">
            <div className="relative inline-block">
              <div className="absolute -inset-3 border border-gold/40 pointer-events-none" />
              <img
                src="/images/academia-formadora.jpg"
                alt="Karina Méndez, formadora en micropigmentación"
                data-testid="academia-formadora-image"
                className="max-h-[380px] lg:max-h-[480px] w-auto object-contain"
              />
            </div>
            <WaveMark className="w-28 text-gold/40 mt-10 hidden lg:block" />
          </Reveal>

          <div className="lg:col-span-7 lg:col-start-6">
            <AnimatePresence mode="wait">
              {status === "done" ? (
                <motion.div
                  key="ok"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className="border border-gold/40 p-10 lg:p-14"
                  data-testid="waitlist-success"
                >
                  <span className="w-14 h-14 rounded-full bg-gold text-ink flex items-center justify-center">
                    <Check size={26} />
                  </span>
                  <h2 className="font-serif text-3xl mt-8">Ya estás dentro.</h2>
                  <p className="text-cream/70 mt-4 leading-relaxed">
                    Gracias por decidirte a tiempo. Te escribiré personalmente — por WhatsApp o email — en el
                    momento en que la Academia abra sus puertas, con tu sorpresa incluida.
                  </p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={submit}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className="border border-gold/25 p-8 sm:p-10 lg:p-14 space-y-8"
                  data-testid="waitlist-form"
                >
                  <Eyebrow dark>Lista de espera</Eyebrow>
                  <div className="grid sm:grid-cols-2 gap-8">
                    <input
                      required
                      value={form.nombre}
                      onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                      placeholder="Nombre"
                      data-testid="waitlist-nombre-input"
                      className={inputCls}
                    />
                    <input
                      required
                      value={form.telefono}
                      onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                      placeholder="Teléfono"
                      data-testid="waitlist-telefono-input"
                      className={inputCls}
                    />
                  </div>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Correo electrónico"
                    data-testid="waitlist-email-input"
                    className={inputCls}
                  />

                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold mb-5">¿Qué te trae por aquí?</p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {INTERESES.map(({ id, label, Icon }) => (
                        <button
                          type="button"
                          key={id}
                          onClick={() => setForm({ ...form, interes: id })}
                          data-testid={`waitlist-interes-${id}`}
                          className={`text-left border p-5 transition-colors duration-300 flex gap-4 items-start ${
                            form.interes === id
                              ? "border-gold bg-gold/10 text-cream"
                              : "border-cream/20 text-cream/60 hover:border-gold/50"
                          }`}
                        >
                          <Icon size={20} className="text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
                          <span className="text-sm leading-snug">{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {error && (
                    <p className="text-sm text-gold" data-testid="waitlist-error">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    data-testid="waitlist-submit-button"
                    className="inline-flex items-center gap-3 rounded-full bg-gold text-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.18em] hover:bg-cream transition-colors duration-300 disabled:opacity-60"
                  >
                    {status === "loading" ? <Loader2 size={15} className="animate-spin" /> : null}
                    Quiero enterarme
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      <section className="bg-cream px-6 sm:px-12 lg:px-20 py-20 lg:py-28" data-testid="academia-preview">
        <Eyebrow>Un adelanto de lo que viene</Eyebrow>
        <div className="grid md:grid-cols-2 gap-px bg-sand border border-sand mt-10">
          <Reveal className="bg-cream">
            <div className="p-8 lg:p-14" data-testid="academia-nivel-fundamentos">
              <span className="font-mono text-xs text-gold tracking-[0.25em]">NIVEL 01</span>
              <h3 className="font-serif text-3xl mt-4 mb-5">Fundamentos</h3>
              <p className="text-sm text-ink/70 leading-relaxed">
                Tu primer paso: aprende a leer el rostro antes de tocarlo. Estructura facial, preparación de la
                piel — con atención especial a pieles sensibles al sol — y las proporciones correctas de cada
                rostro, a través de visagismo, henna y depilación con hilo. Sin aguja, resultado temporal.
              </p>
              <p className="font-serif italic text-lg text-gold mt-6">
                Capta tus primeras clientas antes de dar el salto a la aguja.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="bg-cream">
            <div className="p-8 lg:p-14" data-testid="academia-nivel-avanzado">
              <span className="font-mono text-xs text-gold tracking-[0.25em]">NIVEL 02</span>
              <h3 className="font-serif text-3xl mt-4 mb-5">Avanzado · Técnica NAGI</h3>
              <p className="text-sm text-ink/70 leading-relaxed">
                NAGI Brows y NAGI Lips, pigmentología, maniobra y práctica supervisada. El siguiente paso
                natural después de Fundamentos — con certificado propio de la Técnica NAGI y formación online
                por módulos, a tu ritmo.
              </p>
              <p className="font-serif italic text-lg text-gold mt-6">
                Las técnicas se aprenden. El criterio se forma.
              </p>
            </div>
          </Reveal>
        </div>
        <Reveal className="mt-16 max-w-3xl">
          <p className="font-serif text-2xl sm:text-3xl leading-snug">
            “Cualquiera puede aprender una técnica. <span className="italic text-gold">Pocas desarrollan criterio.</span>”
          </p>
        </Reveal>
      </section>
    </div>
  );
};

export default Academia;
